import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Alert } from '../utils/Alert';
import apiService from '../services/api';
import axios from 'axios';
import { isDeadlineValid } from '../hooks/useIdleTimeout';
import { API_URL } from '../config';
const { auth } = apiService;

interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  role: string;
  avatar?: string;
  full_name?: string;
}

// Define the return types for the auth functions
interface LoginResult {
  success: boolean;
  requiresOtp?: boolean;
  message?: string;
}

interface OtpResult {
  success: boolean;
  message?: string;
}

interface RegisterResult {
  success: boolean;
  requiresOtp?: boolean;
  message?: string;
}

interface FieldCheckResult {
  exists: boolean;
  message: string;
}

interface UserExistsResult {
  success: boolean;
  exists: boolean;
  field?: string;
  message?: string;
  details?: {
    username?: FieldCheckResult;
    email?: FieldCheckResult;
    name?: FieldCheckResult;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
  tempAuthToken: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (userData: any) => Promise<RegisterResult>;
  logout: () => void;
  isOtpVerificationRequired: boolean;
  isOtpVerified: boolean;
  requestOtp: (email: string, tempToken: string) => Promise<OtpResult>;
  verifyOtp: (otp: string) => Promise<OtpResult>;
  userEmail: string | null;
  checkUserExists: (name: string, username: string, email: string) => Promise<UserExistsResult>;
  setAuthState: (token: string, userData: User) => void;
  // Entrance-exam session - deliberately isolated from token/user above (see
  // the comment on entranceToken state) so an abandoned entrance session can
  // never auto-authenticate a later visitor into the real student dashboard.
  entranceToken: string | null;
  setEntranceAuthState: (token: string, userData: Record<string, unknown>) => void;
  clearEntranceAuthStorage: () => void;
  clearEntranceAuthState: () => void;
  // Exam-center invigilator session (Step 1/2 of /entrance-login) - same
  // isolation reasoning as entranceToken: never touches the shared token/
  // user, but still needs to survive a refresh on Step 2 ("Ready to
  // Launch") instead of forcing the invigilator to log in again.
  examCenterToken: string | null;
  examCenterInvigilatorName: string | null;
  setExamCenterAuthState: (token: string, invigilatorName: string) => void;
  clearExamCenterAuthState: () => void;
}

// Checks a token's persisted idle-deadline (see useIdleTimeout) before ever
// treating it as valid - a token sitting in storage doesn't mean the
// session is still alive, since closing the tab kills the in-memory
// setTimeout that would otherwise have caught 30 min of inactivity. This
// is the check that actually catches "closed the tab and came back later",
// which the in-memory timer alone cannot.
function loadTokenIfNotExpired(
  tokenKey: string,
  deadlineKey: string,
  extraKeysToClear: string[] = [],
  storage: Storage = localStorage
): string | null {
  const token = storage.getItem(tokenKey);
  if (!token) return null;
  if (isDeadlineValid(deadlineKey, storage)) return token;
  storage.removeItem(tokenKey);
  storage.removeItem(deadlineKey);
  extraKeysToClear.forEach((key) => storage.removeItem(key));
  return null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  // Seeded once from localStorage at mount, same as `token` above, but kept
  // in its own state slice - entrance pages read THIS, never the shared
  // `token`, so an entrance session can never satisfy the main app's "am I
  // logged in" check.
  //
  // Deliberately NOT deadline-checked here, unlike examCenterToken below -
  // EntranceExam.tsx (the active exam) also reads this token but never
  // refreshes entrance_token_expires_at (idle-timeout is intentionally
  // disabled during an active exam - only its own duration governs that).
  // Gating the token itself here would wrongly clear it on a mid-exam
  // refresh for any exam running past 30 minutes. Instead, the deadline is
  // checked individually by EntranceDashboard/Instructions/Results - the
  // three screens actually in scope for this timeout - not globally here.
  const [entranceToken, setEntranceToken] = useState<string | null>(localStorage.getItem('entrance_token'));
  // Exam-center session lives in sessionStorage, not localStorage - this is
  // a shared kiosk device flow, and closing the tab/browser should always
  // require a fresh invigilator login, never resume where it left off.
  const [examCenterToken, setExamCenterToken] = useState<string | null>(() =>
    loadTokenIfNotExpired('exam_center_token', 'exam_center_token_expires_at', ['exam_center_invigilator_name'], sessionStorage)
  );
  const [examCenterInvigilatorName, setExamCenterInvigilatorName] = useState<string | null>(() =>
    isDeadlineValid('exam_center_token_expires_at', sessionStorage) ? sessionStorage.getItem('exam_center_invigilator_name') : null
  );
  const [isOtpVerificationRequired, setIsOtpVerificationRequired] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [tempAuthToken, setTempAuthToken] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      if (token) {
        try {
          const userData = await auth.getCurrentUser();
          setUser(userData.data);
        } catch (error) {
          console.error('Authentication error:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token]);

  const checkUserExists = async (name: string, username: string, email: string): Promise<UserExistsResult> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await auth.checkUserExists(name, username, email);
      
      // Create a structured response based on the API format
      const result: UserExistsResult = { 
        success: true, 
        exists: response.data.exists,
        field: response.data.field || '',
        message: response.data.message || '',
        details: {
          username: {
            exists: response.data.username_taken || false,
            message: response.data.username_taken ? 'Username is already registered' : 'Username is available'
          },
          email: {
            exists: response.data.email_taken || false,
            message: response.data.email_taken ? 'Email is already registered' : 'Email is available'
          },
          name: {
            exists: false,
            message: 'Name is available'
          }
        }
      };
      
      return result;
    } catch (error: any) {
      console.error('User check error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to check user existence. Please try again.';
      setError(errorMsg);
      return { success: false, exists: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      
      // Extract the token from the response
      // The API returns access_token instead of token
      const token = response.data?.access_token;
      
      if (!token) {
        throw new Error('Invalid response from server');
      }
      
      // Update to store temporary token and email for OTP verification
      setTempAuthToken(token);
      setUserEmail(email);
      setIsOtpVerificationRequired(true);
      
      // Request OTP to be sent to user's email
      await requestOtp(email, token);
      
      return { success: true, requiresOtp: true };
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any): Promise<RegisterResult> => {
    setIsLoading(true);
    setError(null);
    try {
      // First register the user
      await auth.register(userData);
      
      // After successful registration, initiate the login process 
      // which will set up OTP verification
      const loginResult = await login(userData.email, userData.password);
      
      // Return the same format as login to allow redirect to OTP verification
      return {
        success: loginResult.success,
        requiresOtp: loginResult.requiresOtp,
        message: loginResult.message
      };
    } catch (error: any) {
      // More robust error handling
      let errorMessage: string;
      
      if (error.response?.data) {
        // Handle different API error formats
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          // If there are validation errors with multiple fields
          const errorData = error.response.data;
          if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
            // Get the first error message or join all error messages
            const messages = Object.entries(errorData)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            errorMessage = messages || 'Registration failed. Please try again.';
          } else {
            errorMessage = 'Registration failed. Please try again.';
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Registration failed. Please try again.';
      }
      
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Failed to call logout API', err);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    window.location.href = '/login';  // Force a full page reload and redirect to login
  };

  const requestOtp = async (email: string, tempToken: string): Promise<OtpResult> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/auth/request-otp`, 
        { email, purpose: 'registration' },
        {
          headers: {
            'Authorization': `Bearer ${tempToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      return { success: true };
    } catch (error: any) {
      console.error('OTP request error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<OtpResult> => {
    if (!userEmail || !tempAuthToken) {
      Alert.error('Oops...', 'Authentication session expired. Please login again.');
      return { success: false, message: 'Authentication session expired' };
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/verify-otp`,
        { email: userEmail, otp_code: otp, purpose: 'registration' },
        {
          headers: {
            'Authorization': `Bearer ${tempAuthToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      // Extract the token from the response
      // The API returns access_token instead of token
      const token = response.data?.access_token || response.data?.token;
      
      if (!token) {
        throw new Error('Invalid response from server');
      }
      
      // OTP is verified, complete the login process
      localStorage.setItem('token', token);
      setToken(token);
      setIsOtpVerified(true);
      setIsOtpVerificationRequired(false);
      
      // Fetch user profile with new token
      await fetchUserProfile(token);
      
      return { success: true };
    } catch (error: any) {
      console.error('OTP verification error:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid OTP. Please try again.';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserProfile = async (token: string) => {
    try {
      const userData = await auth.getCurrentUser();
      const userDataWithFullName = {
        ...userData.data,
        full_name: userData.data.full_name || userData.data.name // Ensure full_name is included
      };
      setUser(userDataWithFullName);
      localStorage.setItem('user', JSON.stringify(userDataWithFullName));
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
    }
  };

  const setAuthState = (token: string, userData: User) => {
    // Update token
    localStorage.setItem('token', token);
    setToken(token);
    
    // Update user with full_name
    const userDataWithFullName = {
      ...userData,
      full_name: userData.full_name || userData.name // Ensure full_name is included
    };
    setUser(userDataWithFullName);
    localStorage.setItem('user', JSON.stringify(userDataWithFullName));
    
    // Reset OTP-related states
    setIsOtpVerificationRequired(false);
    setIsOtpVerified(true);
  };

  const setEntranceAuthState = (entranceTokenValue: string, userData: Record<string, unknown>) => {
    localStorage.setItem('entrance_token', entranceTokenValue);
    localStorage.setItem('entrance_user', JSON.stringify(userData));
    // Starts the idle-deadline immediately at login, so it's never briefly
    // missing even before the page's own useIdleTimeout hook first runs -
    // that hook keeps sliding this forward on activity from here on.
    localStorage.setItem('entrance_token_expires_at', String(Date.now() + 30 * 60 * 1000));
    setEntranceToken(entranceTokenValue);
  };

  // Clears ONLY localStorage, deliberately leaving the React state alone -
  // used right after a successful exam submit. A shared exam-center device
  // shouldn't have a re-usable token sitting on disk if the student walks
  // away without clicking anything else, but the in-memory state needs to
  // stay valid so "View Details" (same-page navigation, no reload) still
  // works. A refresh or reopen re-seeds state from localStorage (now empty),
  // so the token is genuinely gone at that point - same pattern the main
  // token/user session already relied on before this existed.
  const clearEntranceAuthStorage = () => {
    localStorage.removeItem('entrance_token');
    localStorage.removeItem('entrance_user');
    localStorage.removeItem('entrance_token_expires_at');
  };

  // Full logout - clears both storage and in-memory state. Used for actual
  // exit points (Close button, wrong-identity redirect).
  const clearEntranceAuthState = () => {
    clearEntranceAuthStorage();
    setEntranceToken(null);
  };

  const setExamCenterAuthState = (examCenterTokenValue: string, invigilatorName: string) => {
    sessionStorage.setItem('exam_center_token', examCenterTokenValue);
    sessionStorage.setItem('exam_center_invigilator_name', invigilatorName);
    // Same reasoning as setEntranceAuthState above.
    sessionStorage.setItem('exam_center_token_expires_at', String(Date.now() + 30 * 60 * 1000));
    setExamCenterToken(examCenterTokenValue);
    setExamCenterInvigilatorName(invigilatorName);
  };

  const clearExamCenterAuthState = () => {
    sessionStorage.removeItem('exam_center_token');
    sessionStorage.removeItem('exam_center_invigilator_name');
    sessionStorage.removeItem('exam_center_token_expires_at');
    setExamCenterToken(null);
    setExamCenterInvigilatorName(null);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    token,
    tempAuthToken,
    login,
    register,
    logout,
    isOtpVerificationRequired,
    isOtpVerified,
    requestOtp,
    verifyOtp,
    userEmail,
    checkUserExists,
    setAuthState,
    entranceToken,
    setEntranceAuthState,
    clearEntranceAuthStorage,
    clearEntranceAuthState,
    examCenterToken,
    examCenterInvigilatorName,
    setExamCenterAuthState,
    clearExamCenterAuthState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
